/**
 * Clock on and off. One button, three states.
 *
 * The button is enormous on purpose. This is used one-handed, in a doorway,
 * with a bag in the other hand, and the cost of a mis-tap is a wrong timesheet
 * somebody has to chase down later. Idle is green and says "clock on"; running
 * is coral with a live counter; finished is a calm summary rather than an
 * immediate reset, so the person can see what was recorded.
 *
 * "Am I on right now" is asked of the server first. The v1 contract has no
 * staff-facing endpoint for it, so a 404 there is treated as "this API does not
 * offer it" and the screen falls back to what it remembered in localStorage.
 * That fallback is honest about being local: it never invents a clock-on that
 * the server does not have.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogIn, LogOut, Timer } from 'lucide-react';
import { api, ApiError, qk, type Timesheet } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { fmtClock12, fmtDuration, fmtElapsed, fmtTimeRange, shiftMinutes, today, weekStart } from '../../lib/time.ts';
import { ErrorState, Spinner } from '../../components/Bits.tsx';
import { OutcomeModal, Sheet } from '../../components/Overlay.tsx';

const LOCAL_KEY = 'sp.clock';

function readLocal(): { id: string; clockOn: string } | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    return raw ? (JSON.parse(raw) as { id: string; clockOn: string }) : null;
  } catch {
    return null;
  }
}
function writeLocal(v: { id: string; clockOn: string } | null): void {
  try {
    if (v) window.localStorage.setItem(LOCAL_KEY, JSON.stringify(v));
    else window.localStorage.removeItem(LOCAL_KEY);
  } catch {
    /* storage unavailable; the server check is the real source anyway */
  }
}

export default function ClockPage() {
  const { tenant, me } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  const [breakSheet, setBreakSheet] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [finished, setFinished] = useState<Timesheet | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const current = useQuery({
    queryKey: ['clock', slug],
    queryFn: () => api.clockCurrent(slug),
    enabled: slug !== '',
    // A missing endpoint is a permanent answer, not a transient failure.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    refetchOnWindowFocus: true,
  });

  const endpointMissing = current.error instanceof ApiError && current.error.status === 404;
  const local = readLocal();
  const active: { id: string; clockOn: string } | null = current.data?.timesheet
    ? { id: current.data.timesheet.id, clockOn: current.data.timesheet.clockOn }
    : endpointMissing
      ? local
      : null;

  // Today's shift, so the clock button can say what it is clocking on to.
  const week = weekStart(today());
  const roster = useQuery({
    queryKey: qk.roster(slug, week),
    queryFn: () => api.roster(slug, week),
    enabled: slug !== '',
  });
  const todaysShift = (roster.data?.shifts ?? []).find(
    (s) => s.date === today() && s.userId === me?.user.id && s.status === 'published',
  );

  // One second is the right tick for a counter people watch; anything faster is
  // wasted renders and anything slower reads as frozen.
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  const clockOn = useMutation({
    mutationFn: () => api.clockOn(slug, todaysShift?.id),
    onSuccess: (ts) => {
      writeLocal({ id: ts.id, clockOn: ts.clockOn });
      void qc.invalidateQueries({ queryKey: ['clock', slug] });
      setNow(Date.now());
    },
    onError: (err) => {
      const e = err instanceof ApiError ? err : null;
      if (e?.code === 'already_clocked_on') {
        void qc.invalidateQueries({ queryKey: ['clock', slug] });
        setFailure('You are already clocked on. Pull down to refresh if this screen looks out of date.');
        return;
      }
      setFailure(e?.message ?? 'Could not clock you on. Try again in a moment.');
    },
  });

  const clockOff = useMutation({
    mutationFn: () => api.clockOff(slug, breakMinutes || undefined),
    onSuccess: (ts) => {
      writeLocal(null);
      setBreakSheet(false);
      setBreakMinutes(0);
      setFinished(ts);
      void qc.invalidateQueries({ queryKey: ['clock', slug] });
    },
    onError: (err) => {
      setBreakSheet(false);
      setFailure(err instanceof ApiError ? err.message : 'Could not clock you off. Try again in a moment.');
    },
  });

  if (current.isLoading && !endpointMissing) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-20">
        <Spinner size={24} label="Checking your clock" />
      </div>
    );
  }
  if (current.isError && !endpointMissing) {
    return <ErrorState error={current.error} what="your clock" onRetry={() => void current.refetch()} />;
  }

  const elapsed = active ? now - new Date(active.clockOn).getTime() : 0;
  // How far through the rostered shift they are, drawn as an arc around the
  // disc. Only when there IS a rostered shift to be through: an unrostered
  // clock on has no end to count toward.
  const rosteredMinutes = todaysShift
    ? shiftMinutes(todaysShift.startTime, todaysShift.endTime, todaysShift.breakMinutes)
    : 0;
  const progress = active && rosteredMinutes > 0 ? Math.min(1, elapsed / 60_000 / rosteredMinutes) : 0;
  const ARC = 2 * Math.PI * 47.5;

  return (
    <div className="flex flex-col items-center px-5 py-6">
      <div className="mb-6 text-center">
        <h1 className="text-[20px] font-extrabold tracking-tight text-ink">
          {active ? 'You are on the clock' : 'Ready when you are'}
        </h1>
        <p className="mt-1 text-[13.5px] leading-relaxed text-muted">
          {active
            ? `Started at ${fmtClock12(active.clockOn)}.`
            : todaysShift
              ? `Today you are rostered ${fmtTimeRange(todaysShift.startTime, todaysShift.endTime)}.`
              : 'Nothing rostered for you today. You can still clock on if your manager asked you in.'}
        </p>
      </div>

      {/* The button. Big enough to hit without looking. While on the clock a
          ring breathes behind it and, if there is a rostered shift, an arc
          around the edge fills as the shift goes by. */}
      <div className="relative" style={{ width: 'min(78vw, 300px)', height: 'min(78vw, 300px)' }}>
        {active ? <span className="clock-pulse" aria-hidden="true" /> : null}
        <button
          type="button"
          onClick={() => (active ? setBreakSheet(true) : clockOn.mutate())}
          disabled={clockOn.isPending || clockOff.isPending}
          className="clock-disc relative flex h-full w-full flex-col items-center justify-center"
          style={{
            borderRadius: 'var(--sp-radius-pill)',
            background: `radial-gradient(circle at 32% 26%, var(--sp-gloss-strong), transparent 58%), ${
              active ? 'var(--sp-warn)' : 'var(--sp-accent)'
            }`,
            color: 'var(--sp-on-solid)',
            border: 'none',
            boxShadow: active ? 'var(--sp-shadow-4)' : 'var(--sp-shadow-3)',
            cursor: 'pointer',
          }}
          aria-label={active ? 'Clock off' : 'Clock on'}
        >
          {clockOn.isPending || clockOff.isPending ? (
            <Spinner size={30} label="Working" />
          ) : active ? (
            <>
              <Timer size={30} aria-hidden="true" />
              <span className="tabular mt-2 text-[38px] font-extrabold leading-none">{fmtElapsed(elapsed)}</span>
              <span className="mt-2 flex items-center gap-1.5 text-[15px] font-bold">
                <LogOut size={16} aria-hidden="true" />
                Tap to clock off
              </span>
            </>
          ) : (
            <>
              <LogIn size={34} aria-hidden="true" />
              <span className="mt-3 text-[24px] font-extrabold leading-none">Clock on</span>
              <span className="mt-2 text-[13px] font-semibold opacity-90">{fmtClock12(new Date())}</span>
            </>
          )}
        </button>
        {progress > 0 ? (
          <svg
            className="pointer-events-none absolute inset-0"
            viewBox="0 0 100 100"
            aria-hidden="true"
            style={{ transform: 'rotate(-90deg)' }}
          >
            <circle cx="50" cy="50" r="47.5" fill="none" stroke="var(--sp-on-solid)" strokeOpacity="0.25" strokeWidth="2.5" />
            <circle
              cx="50"
              cy="50"
              r="47.5"
              fill="none"
              stroke="var(--sp-on-solid)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={`${progress * ARC} ${ARC}`}
              style={{ transition: 'stroke-dasharray 1s linear' }}
            />
          </svg>
        ) : null}
      </div>

      {todaysShift ? (
        <div className="card mt-6 flex w-full max-w-xs items-center gap-3 px-3.5 py-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center"
            style={{ borderRadius: 'var(--sp-radius)', background: 'var(--sp-accent-soft)', color: 'var(--sp-accent-strong)' }}
            aria-hidden="true"
          >
            <Timer size={17} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-bold text-ink">
              Rostered {fmtTimeRange(todaysShift.startTime, todaysShift.endTime)}
            </span>
            <span className="tabular block text-[12.5px] text-muted">
              {active
                ? `${fmtDuration(Math.min(rosteredMinutes, Math.round(elapsed / 60_000)))} of ${fmtDuration(rosteredMinutes)} done`
                : `${fmtDuration(rosteredMinutes)} paid`}
            </span>
          </span>
        </div>
      ) : null}

      {endpointMissing ? (
        <p className="mt-5 max-w-xs text-center text-[12px] leading-relaxed text-faint">
          Your clock state is being remembered on this device. If you clock on here and off somewhere else, tell
          your manager so the timesheet can be corrected.
        </p>
      ) : null}

      <Sheet
        open={breakSheet}
        onClose={() => setBreakSheet(false)}
        title="How long was your break?"
        subtitle="Unpaid breaks come off the time you are paid for."
        footer={
          <button
            type="button"
            className="btn btn-primary btn-lg w-full"
            onClick={() => clockOff.mutate()}
            disabled={clockOff.isPending}
          >
            {clockOff.isPending ? <Spinner size={16} label="Clocking off" /> : <LogOut size={16} aria-hidden="true" />}
            Clock off
          </button>
        }
      >
        <div className="grid grid-cols-4 gap-2 py-2">
          {[0, 15, 30, 45].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setBreakMinutes(m)}
              aria-pressed={breakMinutes === m}
              className="flex flex-col items-center justify-center py-3 text-[14px] font-bold"
              style={{
                borderRadius: 'var(--sp-radius)',
                border: `1px solid ${breakMinutes === m ? 'var(--sp-accent)' : 'var(--sp-border)'}`,
                background: breakMinutes === m ? 'var(--sp-accent-soft)' : 'var(--sp-surface)',
                color: breakMinutes === m ? 'var(--sp-accent-strong)' : 'var(--sp-text)',
              }}
            >
              {m === 0 ? 'None' : `${m}m`}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[12.5px] leading-relaxed text-muted">
          You will have worked {fmtDuration(Math.max(0, Math.round(elapsed / 60000) - breakMinutes))} once this is
          taken off. Your manager checks and approves it before it reaches pay.
        </p>
      </Sheet>

      <OutcomeModal
        open={finished !== null}
        onClose={() => setFinished(null)}
        ok
        title="Clocked off"
        message={
          finished
            ? `You worked ${fmtDuration(
                Math.max(
                  0,
                  Math.round(
                    (new Date(finished.clockOff ?? Date.now()).getTime() - new Date(finished.clockOn).getTime()) /
                      60000,
                  ) - finished.breakMinutes,
                ),
              )}, from ${fmtClock12(finished.clockOn)} to ${fmtClock12(finished.clockOff)}. Your manager will approve it before the next pay run.`
            : ''
        }
      />

      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Something went wrong"
        message={failure ?? ''}
      />
    </div>
  );
}
